import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { updateHabit } from '@/lib/firebase/queries/habits';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { habitBadges } from '@/lib/constants/habitBadges';
import { StepsLinkField } from './StepsLinkField';
import type { Habit } from '@/types/habits';

const formSchema = z.object({
  name: z.string().min(2, 'Habit name must be at least 2 characters'),
  category: z.enum(['Exercises', 'Nutrition', 'Stress', 'Sleep', 'Relations', 'Addictions'] as const),
  isPrivate: z.boolean().optional()
});

interface EditHabitDialogProps {
  habit: Habit;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onHabitUpdated: () => void;
}

export function EditHabitDialog({ habit, open, onOpenChange, onHabitUpdated }: EditHabitDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [stepsTarget, setStepsTarget] = useState<number | undefined>(habit.stepsTarget);
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: habit.name,
      category: habit.category,
      isPrivate: habit.isPrivate || false
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setIsLoading(true);
      
      // Automatically select the first bronze badge for the chosen category
      const defaultBadge = habitBadges[values.category].badges.bronze[0].id;
      
      await updateHabit(habit.id, {
        ...values,
        badge: defaultBadge,
        isPrivate: values.isPrivate,
        // Always include the key (even when undefined) so an unlink actually
        // reaches updateHabit's `key present, value undefined` -> deleteField()
        // branch, rather than silently leaving the previously stored target.
        stepsTarget,
      });
      toast.success(t('habits.notifications.habitUpdated'));
      onHabitUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating habit:', error);
      toast.error('Failed to update habit');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`w-screen h-[100dvh] sm:w-[80vw] sm:max-w-md sm:h-auto flex flex-col p-4 sm:p-6 ${isRTL ? 'rtl' : 'ltr'} [&>button]:hidden`}>
        <div className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-4 sm:top-6 z-50`}>
          <DialogClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4 sm:h-5 sm:w-5"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>
        <DialogHeader className={`pb-6 sm:pb-6 ${isRTL ? 'text-right pr-0' : 'text-left pl-0 pr-8 sm:pr-12'}`}>
          <DialogTitle className={`text-xl sm:text-xl ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('habits.editHabit')}
          </DialogTitle>
          <DialogDescription className={`text-base sm:text-base ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('habits.editHabitDesc')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex-1 flex flex-col gap-4 sm:gap-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={`text-sm sm:text-base font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('habits.habitName')}
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={t('habits.habitNamePlaceholder')} 
                      disabled={isLoading} 
                      {...field} 
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className={`h-10 sm:h-11 text-base ${isRTL ? 'text-right' : 'text-left'}`}
                      style={{ fontSize: '16px' }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <StepsLinkField value={stepsTarget} onChange={setStepsTarget} />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={`text-sm sm:text-base font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('habits.category')}
                  </FormLabel>
                  <Select
                    disabled={isLoading}
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger 
                        dir={isRTL ? 'rtl' : 'ltr'} 
                        className={`h-10 sm:h-11 text-sm sm:text-base ${isRTL ? 'text-right' : 'text-left'}`}
                      >
                        <SelectValue placeholder={t('habits.selectCategory')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent dir={isRTL ? 'rtl' : 'ltr'} className={isRTL ? 'text-right' : 'text-left'}>
                      {Object.entries(habitBadges).map(([category, { icon }]) => (
                        <SelectItem key={category} value={category} dir={isRTL ? 'rtl' : 'ltr'}>
                          <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <span>{icon}</span>
                            <span>{t(`habits.categories.${category}`)}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isPrivate"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className={`text-sm sm:text-base font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('habits.privateHabit')}
                    </FormLabel>
                    <div className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('habits.privateHabitDesc')}
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isLoading}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-2 mt-auto pt-4 sm:pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                className="w-full sm:w-auto h-11 sm:h-10 text-sm sm:text-base order-2 sm:order-1"
              >
                {t('common.cancel')}
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading}
                className="w-full sm:w-auto h-11 sm:h-10 text-sm sm:text-base order-1 sm:order-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} />
                    {t('common.saving')}
                  </>
                ) : (
                  t('common.saveChanges')
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}